import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, PenLine, Loader2, ArrowLeft, Sparkles, ArrowRight } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi } from "./adminApi";

export default function NewListingChoice() {
  const nav = useNavigate();
  const [mode, setMode] = useState(null); // null | "online"
  const [o, setO] = useState({ owner_nom: "", owner_prenom: "", owner_telephone: "", owner_email: "", url: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setO((p) => ({ ...p, [k]: v }));

  const inp = "w-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors";
  const lbl = "block text-xs uppercase tracking-wider text-zinc-400 mb-1.5";

  const generate = async (e) => {
    e.preventDefault();
    setErr("");
    if (!o.url.trim().startsWith("http")) { setErr("Merci de saisir une URL valide (https://…)."); return; }
    setLoading(true);
    try {
      const data = await adminApi.importUrl(o.url.trim());
      nav("/admin/new/manual", { state: { prefill: {
        ...data,
        owner_nom: o.owner_nom, owner_prenom: o.owner_prenom,
        owner_telephone: o.owner_telephone, owner_email: o.owner_email,
      } } });
    } catch (ex) { setErr(ex.message || "Analyse de l'annonce impossible."); }
    finally { setLoading(false); }
  };

  return (
    <AdminShell
      title="Créer un nouveau bien"
      action={<button onClick={() => nav("/admin")} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 px-4 py-2.5"><ArrowLeft className="w-4 h-4" /> Retour</button>}
    >
      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 mb-6" data-testid="choice-error">{err}</div>}

      {mode === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {/* ON MARKET */}
          <button
            onClick={() => setMode("online")}
            data-testid="choice-on-market"
            className="group text-left border border-white/10 hover:border-[#C19A6B]/50 bg-white/[0.02] hover:bg-[#C19A6B]/[0.04] p-8 transition-colors flex flex-col gap-4"
          >
            <div className="w-12 h-12 bg-[#C19A6B]/15 text-[#C19A6B] flex items-center justify-center"><Globe className="w-6 h-6" /></div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#C19A6B] font-semibold">ON MARKET</span>
              <h3 className="text-xl font-light text-white">Générer depuis une annonce en ligne</h3>
              <p className="text-sm text-zinc-400 font-light leading-relaxed">Collez l'URL d'une annonce existante : l'IA récupère automatiquement les informations et les photos pour préremplir la fiche OFF MARKETTE.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#C19A6B] group-hover:gap-2.5 transition-all mt-auto">Commencer <ArrowRight className="w-4 h-4" /></span>
          </button>

          {/* OFF MARKET */}
          <button
            onClick={() => nav("/admin/new/manual")}
            data-testid="choice-off-market"
            className="group text-left border border-white/10 hover:border-[#C19A6B]/50 bg-white/[0.02] hover:bg-[#C19A6B]/[0.04] p-8 transition-colors flex flex-col gap-4"
          >
            <div className="w-12 h-12 bg-white/5 text-white flex items-center justify-center"><PenLine className="w-6 h-6" /></div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-semibold">OFF MARKET</span>
              <h3 className="text-xl font-light text-white">Saisie manuelle complète</h3>
              <p className="text-sm text-zinc-400 font-light leading-relaxed">Renseignez directement toutes les informations du bien (bien confidentiel, jamais diffusé ailleurs).</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-300 group-hover:gap-2.5 transition-all mt-auto">Créer manuellement <ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>
      ) : (
        <div className="max-w-2xl">
          <button onClick={() => setMode(null)} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Choix du mode</button>
          <form onSubmit={generate} className="border border-white/10 p-8 space-y-6" data-testid="on-market-form">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#C19A6B] font-semibold">ON MARKET</span>
              <h3 className="text-xl font-light text-white">Générer l'annonce depuis une URL</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Nom du vendeur</label><input value={o.owner_nom} onChange={(e) => set("owner_nom", e.target.value)} className={inp} data-testid="on-owner-nom" /></div>
              <div><label className={lbl}>Prénom</label><input value={o.owner_prenom} onChange={(e) => set("owner_prenom", e.target.value)} className={inp} data-testid="on-owner-prenom" /></div>
              <div><label className={lbl}>Téléphone</label><input value={o.owner_telephone} onChange={(e) => set("owner_telephone", e.target.value)} className={inp} data-testid="on-owner-tel" /></div>
              <div><label className={lbl}>E-mail</label><input type="email" value={o.owner_email} onChange={(e) => set("owner_email", e.target.value)} className={inp} data-testid="on-owner-email" /></div>
            </div>

            <div>
              <label className={lbl}>URL de l'annonce existante *</label>
              <input required value={o.url} onChange={(e) => set("url", e.target.value)} className={inp} placeholder="https://www.exemple.fr/annonce/..." data-testid="on-url" />
            </div>

            <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm py-3.5 transition-colors disabled:opacity-60" data-testid="on-generate-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours (~30s)…</> : <><Sparkles className="w-4 h-4" /> Générer l'annonce</>}
            </button>
            <p className="text-xs text-zinc-500 font-light">L'IA préremplira la fiche. Vous pourrez tout modifier avant de la mettre en ligne. La fiche sera créée <strong>hors ligne (brouillon)</strong> par défaut.</p>
          </form>
        </div>
      )}
    </AdminShell>
  );
}
